<template>
  <div class="owc-hotspot-simple-item">
    <div v-if="isEditMode" class="owc-hotspot-simple-item__author-aem">
      <wb-icon name="bds/hotspot-active/24"></wb-icon>
      <p>Hotspot: {{ itemData.hotspotText }}</p>
    </div>
    <wb-tooltip-host
      v-else
      class="owc-hotspot-simple-item__spot"
      :class="{ 'owc-hotspot-simple-item__spot--active': isActive }"
      :open="isActive"
      @click="onHotspotItemClick"
      :position="tooltipPosition"
      :style="{
        '--coordinate-x': `${itemData.coordinateX}%`,
        '--coordinate-y': `${itemData.coordinateY}%`,
      }"
    >
      <span class="owc-hotspot-simple-item__spot-dot"></span>
      <wb-tooltip class="owc-hotspot-simple-item__tooltip" hidden>
        {{ itemData.hotspotText }}
      </wb-tooltip>
    </wb-tooltip-host>
  </div>
</template>

<script lang="ts">
import { HotspotSimpleItemComponent } from '@owc-packages/Hotspot/src/HotspotSimpleItem.d';
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import HotspotMixin from '@owc-utils/Mixins/HotspotMixin';
import { calculateSimpleItemTooltipPosition } from './HotspotUtils';
import { HotspotState } from '@owc-types/Hotspot';
import AEM from '@owc-utils/Mixins/AEM';
/**
 * TODO: Documentation
 * Hotspot Simple Item
 *
 * A single hotspot to be places in the HotspotSimple.
 * Has a container only visible on AEM in edit-mode to create a click area for the author.
 */
export default (ComponentInitializeDispatcher as HotspotSimpleItemComponent).extend({
  name: 'owc-hotspot-simple-item',
  mixins: [HotspotMixin, AEM],
  props: {
    item: {
      type: Object,
      required: false,
    },
    isRtl: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isActive: false,
    };
  },
  computed: {
    itemData() {
      // Item in edit mode should take data from the store as we render it as web component.
      // Otherwise, data should be taken from props as it's rendering as Vue component.
      return this.isEditMode ? this.componentData : this.item;
    },
    parentComponentIdentifier: function() {
      return this.itemData?.identifiers?.componentParentId || '';
    },
    /**
     * Returning position of wb-tooltip-host depending on hotspot position
     *
     * If the x- or y-position (%) of the hotspot is below or above a specific value (%),
     * the wb-tooltip position will be adapted.
     *
     * Defaults to position top
     */
    tooltipPosition: function() {
      return calculateSimpleItemTooltipPosition(this.itemData, this.isRtl);
    },
  },
  beforeDestroy() {
    this.removeHotspotChangeListener(this.componentId, this.toggleActiveFlag);
  },
  mounted() {
    this.addHotspotChangeListener(this.componentId, this.toggleActiveFlag);
  },
  methods: {
    onHotspotItemClick() {
      if (this.isActive) {
        this.setActiveHotspotId(this.parentComponentIdentifier, null);
      } else {
        this.setActiveHotspotId(this.parentComponentIdentifier, this.componentId!);
      }
    },
    deactivate() {
      this.isActive = false;
    },
    activate() {
      this.isActive = true;
    },
    toggleActiveFlag(state: HotspotState) {
      const parentActiveItemId = state.activeItemsMap[this.parentComponentIdentifier];

      // Active item id is deliberately set to null, deactivate
      if (parentActiveItemId === null && this.isActive) {
        this.deactivate();
        return;
      }

      // Active item id exists for the given parent
      if (parentActiveItemId) {
        if (parentActiveItemId === this.componentId) {
          this.activate();
        } else {
          this.deactivate();
        }
      }
    },
  },
});
</script>
<style lang="scss">
@import 'HotspotItem';

$moduleName: 'owc-hotspot-simple-item';

.#{$moduleName} {
  @include hotspot-item($moduleName);

  &__spot {
    position: absolute;
    top: calc(var(--coordinate-y) - #{math.div($hotSpotSize, 2)});
    left: calc(var(--coordinate-x) - #{math.div($hotSpotSize, 2)});

    z-index: 2;
  }
}
</style>
